import type { UflrType } from 'lib/uflr';
import type { IFolderFlags } from 'reactApp/modules/home/home.types';
import type { EStorageType } from 'reactApp/modules/storage/storage.types';
import type { Correspondents } from 'reactApp/types/QuotaCleaner';
import type { Count, Kind, Sort, SubKind, Url, VirusScan } from 'reactApp/types/Tree';
import type { IPublicFolder } from 'server/types/context/IPublicFolder';

export interface WeblinkDownloadsCounts {
    count_downloads_total?: number;
    count_downloads_left?: number;
}

export interface WeblinkDownloadable extends WeblinkDownloadsCounts {
    weblinkDownloadable?: boolean;
}

export interface PublicFolder extends WeblinkDownloadable {
    type?: string;
    id: string;
    weblink: string;
    home?: string;
    name: string;
    size?: number;
    sizeWithoutRestricted?: number;
    kind: Kind;
    parent: string;
    isFolder: true;
    rev?: number;
    count: Count;
    isLoading: boolean;
    isLoaded: boolean;
    hasMoreToLoad: boolean;
    childs: string[];
    authors?: Record<string, number>;
    storage: EStorageType.public;
    sort?: Sort;
    flags?: IFolderFlags;
    __reduxTree?: true;
    error?: string;
    cursor?: string;
    correspondents?: Correspondents;
    weblinkSeoEnabled?: boolean;
    weblinkEnabledDisabledDownload?: boolean;
    uniqueId?: string;
    mode_bitmask?: number;
}

export interface PublicFile {
    id: string;
    weblink: string;
    home?: string;
    name: string;
    ext: string;
    author?: string;
    nameWithoutExt: string;
    size: number;
    kind: Kind;
    subKind: SubKind;
    parent: string;
    isFolder: false;
    weblinkAccessRights?: string;
    weblinkDownloadable?: boolean;
    weblinkSeoEnabled?: boolean;
    weblinkEnabledDisabledDownload?: boolean;
    uniqueId?: string;
    count_downloads_total?: number;
    count_downloads_left?: number;
    virusScan: VirusScan;
    mtime: number;
    hash?: string;
    uflr?: UflrType;
    storage: EStorageType.public;
    thumbnails?: Record<string, string>;
    url?: Url;
    __reduxTree?: true;
    mode_bitmask?: number;
    correspondents?: Correspondents;
}

export type PublicItem = PublicFolder | PublicFile;

export interface IRootWeblink extends WeblinkDownloadsCounts {
    weblink: string;
    name: string;
    isOwn: boolean;
    isUploadEnabled: boolean;
    uploadLimit?: number;
    weblinkExpires?: number;
    albumId?: string;
    isAutodelete?: boolean;
    weblinkDownloadable?: boolean;
    pin?: string;
    weblinkSeoEnabled?: boolean;
    weblinkEnabledDisabledDownload?: boolean;
    downloads?: number;
    views?: number;
}

export interface DownloadLimit {
    left: number;
    total: number;
    next_reset: number;
}
export interface PublicState {
    rootWeblink: IRootWeblink;
    owner?: {
        email: string;
    };
    forceShowDownloadButton: boolean;
    list: Record<string, PublicItem>;
    isOpenDwhRadarSent?: boolean;
    redirectFromViewer?: boolean;
    publicPasswords: Record<string, string>;
    downloadLimits: Record<string, DownloadLimit>;
}

export interface ILoadMoreRequestData {
    id: string;
    offset: number;
}

export const isPublicApiResponseType = (item: any): item is IPublicFolder => item.list;

export type PublicSetPin = {
    pin: string;
    public_id: string;
    onSuccess: () => void;
    onError?: () => void;
};

export type PublicUpdatePin = Omit<PublicSetPin, 'onSuccess'>;

export type PublicDeletePin = {
    public_id: string;
    onSuccess: () => void;
    onRestore: () => void;
};

export type PublicCheckPin = {
    pin: string;
    onSuccess: () => void;
    onFail: (payload: { isBlocked: boolean; blockTimeout: number }) => void;
};

export type PublicSetDownloadLimit = {
    public_id: string;
    limit: number;
};

export type PublicInfoDownloadLimit = {
    download_limit: DownloadLimit;
    public_id: string;
};

export const enum EMoreButtonPublicLocation {
    headerMenuDots = 'header_menu_dots',
    fileMenuDots = 'file_menu_dots',
}

export const enum EPubAuthBannerAction {
    show = 'show',
    clickAuth = 'click-auth',
    clickDownload = 'click-download',
    close = 'close',
    attemptToShow = 'attempt-to-show',
}

export const enum EPubAuthBannerCloseMethod {
    tapOutside = 'tap_outside',
    closeButton = 'close_button',
    laterButton = 'later_button',
}
